import React, { useMemo, useState } from 'react';
import T from 'prop-types';
import { Alert, Box, Button, Typography } from '@mui/material';
import { Dialog } from 'web-components-ts';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { apiGatewaysV2DeleteGateway } from 'api/gateways_v2';
import { COLOR_ERROR } from 'attrs/colors';
import { addNotification } from 'redux/ui/notifications/actions';
import { actionStatus, getErrorMessage } from 'redux/utils';
import { generateId } from 'helpers/utils';

const GatewayDeleteDialog = ({ gateway, onClose, onDeleted }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const cancelLabel = t('default_actions.cancel');

  const hasLinkedMachines = (gateway?.machine_count || 0) > 0;

  const deleteWarningMessage = useMemo(
    () =>
      hasLinkedMachines
        ? t('gateways.crud.delete.warning_with_machines')
        : t('gateways.crud.delete.warning'),
    [hasLinkedMachines, t]
  );

  const handleDelete = async () => {
    setIsDeleting(true);
    setErrorMessage('');

    try {
      await apiGatewaysV2DeleteGateway(gateway?.serial_number);

      dispatch(
        addNotification({
          key: generateId(),
          type: actionStatus.SUCCESS,
          message: 'gateways.crud.notifications.delete_success'
        })
      );

      onDeleted();
    } catch (err) {
      const error = getErrorMessage(err);
      const isLinkedMachinesError = err?.response?.status === 400;

      setErrorMessage(
        isLinkedMachinesError
          ? t('gateways.crud.notifications.delete_linked_machines_error')
          : t(
              `constant_messages.gateway_v2.error.${error.message}`,
              { defaultValue: t('gateways.crud.notifications.generic_error') }
            )
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" sx={{ color: '#7A7D85' }}>
        {deleteWarningMessage}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {t('gateways.crud.delete.continue_question')}
      </Typography>
      {errorMessage && (
        <Alert severity="error" data-test-id="delete-gateway-error">
          {errorMessage}
        </Alert>
      )}
    </Box>
  );

  const actionBar = (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }} data-test-id="delete-gateway-actions">
      <Button
        variant="text"
        onClick={onClose}
        disabled={isDeleting}
        data-test-id="delete-gateway-cancel-button"
      >
        {cancelLabel}
      </Button>
      <Button
        variant="contained"
        color="error"
        onClick={handleDelete}
        disabled={isDeleting}
        sx={{ minWidth: 148, backgroundColor: COLOR_ERROR }}
        data-test-id="delete-gateway-confirm-button"
      >
        {t('gateways.crud.delete.confirm')}
      </Button>
    </Box>
  );

  return (
    <Dialog
      open
      handleClose={onClose}
      title={t('gateways.crud.delete.title')}
      content={content}
      actionBar={actionBar}
      width="100%"
      maxWidth="580px"
      paddingXContent="24px"
      paddingYContent="8px 8px 0"
      data-test-id="delete-gateway-dialog"
    />
  );
};

GatewayDeleteDialog.propTypes = {
  gateway: T.shape({
    machine_count: T.number,
    serial_number: T.string
  }).isRequired,
  onClose: T.func.isRequired,
  onDeleted: T.func.isRequired
};

export default GatewayDeleteDialog;
